import moment from "moment";
import {
  BankIncome,
  IncomeDocument,
  NetIncomeSummary,
  PayrollItem
} from "types/landlord/reports";
import {
  centsToAmount,
  mapTablePaystubData
} from "views/applications-id-report/utils";
import { chartColors } from "../components/constants";
import { Chart } from "../components/Chart";
import { Show } from "shared/components";
import { InfoCircle } from "untitledui-js-base";
import { getInfoTextByDocumentType } from "views/applications-id-report/utils/getDisclaimerTextByDocType";

interface VerifiedIncomeGraphProps {
  bankIncome?: BankIncome[];
  document?: IncomeDocument;
  net_income_summary?: NetIncomeSummary[];
  items?: PayrollItem[];
  incomeSourceType: "bank" | "document" | "payroll";
}

export const VerifiedIncomeGraph = ({
  bankIncome,
  net_income_summary,
  document,
  items,
  incomeSourceType
}: VerifiedIncomeGraphProps) => {
  const isVerifiedBankIncome = incomeSourceType === "bank";

  const isVerifiedDocumentIncome = incomeSourceType === "document";

  const isVerifiedPayrollIncome = incomeSourceType === "payroll";

  const incomeData = bankIncome?.flatMap((bi, biIndex) => {
    return bi.items.flatMap((item) => {
      return item.bank_income_sources.map((bis, bisIndex) => ({
        id: bis.income_description,
        color:
          chartColors[biIndex >= bisIndex ? biIndex : bisIndex] ??
          chartColors[0],
        data: bis.historical_summary.map((hs) => ({
          x: moment(hs.start_date).format("MMM YY"),
          y: centsToAmount(hs.total_amount?.cents || 0) // TODO Max check this 0 value
        }))
      }));
    });
  });

  const payrollIncome = items
    ?.flatMap((item) => item.payroll_income)
    .map((income, index) => ({
      id: income.pay_stubs[0]?.employer?.name,
      color: chartColors[index] ?? chartColors[0],
      data: mapTablePaystubData(income.pay_stubs)
        .reverse()
        .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf())
        .map((row: { date: string; total_net: number }) => ({
          x: row.date,
          y: centsToAmount(row.total_net)
        }))
    }));

  const documentIncome = (net_income_summary || []).reduce(
    (acc, entry) => {
      let currentIndex = acc.length;
      entry.sources.map((source) => {
        const formattedMonth = moment(entry.month, "YYYY-MM").format("MMM YY");

        let existingSource = acc.find((item) => item.id === source.description);

        if (!existingSource) {
          existingSource = {
            id: source.description || "Unknown Source",
            color: chartColors[currentIndex % chartColors.length],
            data: []
          };
          acc.push(existingSource);
          currentIndex++;
        }

        const existingMonth = existingSource.data.find(
          (dataPoint) => dataPoint.x === formattedMonth
        );

        if (existingMonth)
          existingMonth.y += (source.net_pay?.cents || 0) / 100;
        else
          existingSource.data.push({
            x: formattedMonth,
            y: (source.net_pay?.cents || 0) / 100
          });
      });

      const normalizedKeys = Object.keys(
        acc.reduce<Record<string, 1>>((acc, d) => {
          d.data.forEach((item) => {
            acc[item.x] = 1;
          });
          return acc;
        }, {})
      );
      return acc.map((d) => ({
        ...d,
        data: normalizedKeys
          .reduce<{ x: string; y: number }[]>((acc, x) => {
            acc.push(d.data.find((item) => item.x === x) || { x, y: 0 });
            return acc;
          }, [])
          .sort(
            (a, b) =>
              moment(a.x, "MMM YY").valueOf() - moment(b.x, "MMM YY").valueOf()
          )
      }));
    },
    [] as { id: string; color: string; data: { x: string; y: number }[] }[]
  );

  const chartData = isVerifiedBankIncome
    ? incomeData
    : isVerifiedPayrollIncome
      ? payrollIncome
      : isVerifiedDocumentIncome
        ? documentIncome
        : [];

  return (
    <>
      <Show if={!chartData?.length}>
        <div className="flex items-center justify-center">
          <p className="text-sm text-content-400">There is no data available</p>
        </div>
      </Show>
      <Show if={Boolean(chartData?.length)}>
        <div
          className={`pb-5 grid ${
            (chartData?.length || 0) < 4
              ? "flex justify-end"
              : "grid-cols-2 gap-2"
          }`}
        >
          {chartData?.map((line) => (
            <div key={line.id} className="w-full">
              <span
                className="inline-block w-2 h-2 rounded-full mr-2"
                style={{
                  backgroundColor: line.color
                }}
              />
              <span className="text-xs text-content-1000">{line.id}</span>
            </div>
          ))}
        </div>
        <div className="h-[205px]">
          <Chart data={chartData || []} />
        </div>
        <Show
          if={Boolean(
            incomeSourceType === "document" && document?.type !== "pay_stub"
          )}
        >
          <div className="flex gap-4 md:items-center pt-6 border-t border-alpha-100">
            <InfoCircle size="20" className="shrink-0 stroke-content-100" />
            <p className="text-content-400 font-medium text-sm">
              {getInfoTextByDocumentType(document?.type)}
            </p>
          </div>
        </Show>
      </Show>
    </>
  );
};
